/**
 * Which shipment documents can be shown in the browser.
 *
 * Uploads are limited to jpg/jpeg/png/pdf/docx/doc/xls/xlsx/csv
 * (FirebaseStorageService::ALLOWED_EXTENSIONS). Images and PDFs render natively from
 * a blob URL; Office formats cannot, and the usual Google/Office web viewers need a
 * publicly reachable URL, which private Firebase Storage objects never have. Those
 * stay download-only.
 */
export type DocumentPreviewKind = "image" | "pdf" | null;

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png"];

const extensionOf = (fileName: string): string =>
  fileName.split(".").pop()?.toLowerCase() ?? "";

/**
 * Prefers the server-provided MIME type and falls back to the file extension,
 * since mimeType is nullable on ShipmentDocumentView.
 */
export const documentPreviewKind = (
  originalName: string,
  mimeType?: string | null,
): DocumentPreviewKind => {
  const mime = mimeType?.toLowerCase() ?? "";
  if (mime.startsWith("image/")) {
    return IMAGE_EXTENSIONS.some((ext) => mime.endsWith(ext)) ? "image" : null;
  }
  if (mime === "application/pdf") return "pdf";

  const ext = extensionOf(originalName);
  if (IMAGE_EXTENSIONS.includes(ext)) return "image";
  if (ext === "pdf") return "pdf";

  return null;
};

export const isDocumentPreviewable = (
  originalName: string,
  mimeType?: string | null,
): boolean => documentPreviewKind(originalName, mimeType) !== null;
